import { GridPaginationModel } from "@mui/x-data-grid";
import { useCallback, useEffect, useRef, useState } from "react";
import { UseDataGridPaginationProps } from "../../Interfaces/CustomDataGrid.interfaces";
import { useTableStateUrl } from "./useTableStateUrl";

export const useDataGridPagination = ({
  initialPaginationModel,
  onPaginationChange,
  enableUrlParams = true
}: UseDataGridPaginationProps = {}) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel | undefined>(initialPaginationModel);
  const isInitializedRef = useRef(false);
  const isUpdatingFromUrlRef = useRef(false);

  // Shared URL state management
  const { getTableStateFromUrl, updateTableStateInUrl, clearTableStateFromUrl } = useTableStateUrl(enableUrlParams);

  const updateUrlParams = useCallback(
    (paginationModel: GridPaginationModel) => {
      const paginationData = {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize
      };

      // Only include pagination if it's not default (page 0, pageSize 100)
      const paginationState =
        paginationModel.page !== 0 || paginationModel.pageSize !== 100
          ? { pagination: paginationData }
          : { pagination: undefined };

      updateTableStateInUrl(paginationState, isUpdatingFromUrlRef.current);
    },
    [updateTableStateInUrl]
  );

  const handlePaginationChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
      onPaginationChange?.(model);

      if (enableUrlParams && !isUpdatingFromUrlRef.current) {
        updateUrlParams(model);
      }
    },
    [enableUrlParams, updateUrlParams, onPaginationChange]
  );

  const resetToFirstPage = useCallback(() => {
    setPaginationModel((currentModel) => {
      const updatedModel: GridPaginationModel = {
        page: 0,
        pageSize: currentModel?.pageSize ?? 100
      };

      onPaginationChange?.(updatedModel);

      if (enableUrlParams && !isUpdatingFromUrlRef.current) {
        updateUrlParams(updatedModel);
      }

      return updatedModel;
    });
  }, [onPaginationChange, enableUrlParams, updateUrlParams]);

  // Initialize pagination from URL params
  useEffect(() => {
    if (!enableUrlParams || isInitializedRef.current) return;

    const tableState = getTableStateFromUrl();
    if (tableState?.pagination) {
      try {
        isUpdatingFromUrlRef.current = true;
        setPaginationModel(tableState.pagination);
      } catch (error) {
        console.warn("Failed to parse URL pagination parameters:", error);
        clearTableStateFromUrl();
      } finally {
        isUpdatingFromUrlRef.current = false;
      }
    }

    isInitializedRef.current = true;
  }, [enableUrlParams, getTableStateFromUrl, clearTableStateFromUrl, initialPaginationModel]);

  useEffect(() => {
    if (initialPaginationModel && !isInitializedRef.current) {
      setPaginationModel(initialPaginationModel);
    }
  }, [initialPaginationModel]);

  return {
    paginationModel,
    handlePaginationChange,
    resetToFirstPage,
    updateUrlParams
  };
};
